import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  projectsAPI, scenariosAPI, analyzerAPI, dashboardAPI,
  marketAPI, reportsAPI, companyAPI, settingsAPI, institutionAPI,
} from './api';

// ═══════════════════════════════════════════
//  QUERY KEYS — centralized for invalidation
// ═══════════════════════════════════════════

export const queryKeys = {
  projects: ['projects'],
  project: (id) => ['project', id],
  scenarios: (projectId) => ['scenarios', projectId],
  scenario: (projectId, scenarioId) => ['scenario', projectId, scenarioId],
  analysis: (scenarioId) => ['analysis', scenarioId],
  analysisRuns: (projectId) => ['analysisRuns', projectId],
  dashboardStats: ['dashboardStats'],
  decisions: (projectId, scenarioId) => ['decisions', projectId, scenarioId],
  projectScore: (projectId, scenarioId) => ['projectScore', projectId, scenarioId],
  comparables: (projectId) => ['comparables', projectId],
  marketSuggestions: (projectId, scenarioId) => ['marketSuggestions', projectId, scenarioId],
  marketAnalysis: (projectId, scenarioId) => ['marketAnalysis', projectId, scenarioId],
  marketDerived: (projectId, scenarioId) => ['marketDerived', projectId, scenarioId],
  marketRadius: (projectId) => ['marketRadius', projectId],
  marketLocationData: (projectId) => ['marketLocationData', projectId],
  marketSnapshot: (projectId, scenarioId) => ['marketSnapshot', projectId, scenarioId],
  marketCrossValidation: (projectId, scenarioId) => ['marketCrossValidation', projectId, scenarioId],
  reports: (projectId) => ['reports', projectId],
  companyUsers: ['companyUsers'],
  entitlements: ['entitlements'],
  settings: ['settings'],
  copilotHistory: (projectId, scenarioId) => ['copilotHistory', projectId, scenarioId],
  structuredInputs: (scenarioId) => ['structuredInputs', scenarioId],
};

// ═══════════════════════════════════════════
//  PROJECTS
// ═══════════════════════════════════════════

export function useProjects() {
  return useQuery({
    queryKey: queryKeys.projects,
    queryFn: () => projectsAPI.list().then(r => r.data),
  });
}

export function useProject(id) {
  return useQuery({
    queryKey: queryKeys.project(id),
    queryFn: () => projectsAPI.get(id).then(r => r.data),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => projectsAPI.create(data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.projects });
      qc.invalidateQueries({ queryKey: queryKeys.dashboardStats });
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => projectsAPI.update(id, data).then(r => r.data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.projects });
      qc.invalidateQueries({ queryKey: queryKeys.project(id) });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => projectsAPI.delete(id).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.projects });
      qc.invalidateQueries({ queryKey: queryKeys.dashboardStats });
    },
  });
}

// ═══════════════════════════════════════════
//  SCENARIOS
// ═══════════════════════════════════════════

export function useScenarios(projectId) {
  return useQuery({
    queryKey: queryKeys.scenarios(projectId),
    queryFn: () => scenariosAPI.list(projectId).then(r => r.data),
    enabled: !!projectId,
  });
}

export function useCreateScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }) => scenariosAPI.create(projectId, data).then(r => r.data),
    onSuccess: (_, { projectId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.scenarios(projectId) });
      qc.invalidateQueries({ queryKey: queryKeys.dashboardStats });
    },
  });
}

export function useDeleteScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, scenarioId }) => scenariosAPI.delete(projectId, scenarioId).then(r => r.data),
    onSuccess: (_, { projectId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.scenarios(projectId) });
    },
  });
}

export function useDuplicateScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, scenarioId }) => scenariosAPI.duplicate(projectId, scenarioId).then(r => r.data),
    onSuccess: (_, { projectId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.scenarios(projectId) });
    },
  });
}

// ═══════════════════════════════════════════
//  ANALYZER
// ═══════════════════════════════════════════

// SWA-240 · previsualización: calcula y cachea, pero NO invalida la lista de
// corridas ni el dashboard, porque no se registró ninguna corrida.
export function usePreviewAnalysis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => analyzerAPI.preview(data).then(r => r.data),
    onSuccess: (result, variables) => {
      qc.setQueryData(queryKeys.analysis(variables.scenario_id), result);
    },
  });
}

export function useRunAnalysis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => analyzerAPI.run(data).then(r => r.data),
    onSuccess: (result, variables) => {
      // Cache the result directly
      qc.setQueryData(queryKeys.analysis(variables.scenario_id), result);
      // Invalidate related data
      qc.invalidateQueries({ queryKey: queryKeys.dashboardStats });
      qc.invalidateQueries({ queryKey: queryKeys.decisions(variables.project_id, variables.scenario_id) });
      qc.invalidateQueries({ queryKey: queryKeys.projectScore(variables.project_id, variables.scenario_id) });
      qc.invalidateQueries({ queryKey: queryKeys.analysisRuns(variables.project_id) });
    },
  });
}

export function useAnalysisRuns(projectId) {
  return useQuery({
    queryKey: queryKeys.analysisRuns(projectId),
    queryFn: () => analyzerAPI.getRuns(projectId).then(r => r.data),
    enabled: !!projectId,
  });
}

export function useSensitivity() {
  return useMutation({
    mutationFn: (data) => analyzerAPI.sensitivity(data).then(r => r.data),
  });
}

export function useTornado() {
  return useMutation({
    mutationFn: (data) => analyzerAPI.tornado(data).then(r => r.data),
  });
}

export function useCompareScenarios() {
  return useMutation({
    mutationFn: (data) => analyzerAPI.compare(data).then(r => r.data),
  });
}

// ═══════════════════════════════════════════
//  DASHBOARD
// ═══════════════════════════════════════════

export function useDashboardStats() {
  return useQuery({
    queryKey: queryKeys.dashboardStats,
    queryFn: () => dashboardAPI.getStats().then(r => r.data),
    staleTime: 30 * 1000,
  });
}

export function useDecisions(projectId, scenarioId) {
  return useQuery({
    queryKey: queryKeys.decisions(projectId, scenarioId),
    queryFn: () => dashboardAPI.getDecisions(projectId, scenarioId).then(r => r.data),
    enabled: !!projectId,
    staleTime: 0,
  });
}

export function useProjectScore(projectId, scenarioId) {
  return useQuery({
    queryKey: queryKeys.projectScore(projectId, scenarioId),
    queryFn: () => dashboardAPI.getProjectScore(projectId, scenarioId).then(r => r.data),
    enabled: !!projectId,
  });
}

// ═══════════════════════════════════════════
//  MARKET INTELLIGENCE
// ═══════════════════════════════════════════

export function useComparables(projectId) {
  return useQuery({
    queryKey: queryKeys.comparables(projectId),
    queryFn: () => marketAPI.listComparables(projectId).then(r => r.data),
    enabled: !!projectId,
  });
}

export function useCreateComparable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => marketAPI.createComparable(data).then(r => r.data),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.comparables(variables.project_id) });
    },
  });
}

export function useDeleteComparable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, projectId }) => marketAPI.deleteComparable(id).then(r => r.data),
    onSuccess: (_, { projectId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.comparables(projectId) });
    },
  });
}

export function useMarketSuggestions(projectId, scenarioId, enabled = true) {
  return useQuery({
    queryKey: queryKeys.marketSuggestions(projectId, scenarioId),
    queryFn: () => marketAPI.getSuggestions(projectId, scenarioId).then(r => r.data),
    enabled: !!projectId && !!scenarioId && enabled,
    staleTime: 60 * 1000,
  });
}

export function useMarketAnalysis(projectId, scenarioId) {
  return useQuery({
    queryKey: queryKeys.marketAnalysis(projectId, scenarioId),
    queryFn: () => marketAPI.getAnalysis(projectId, scenarioId).then(r => r.data),
    enabled: !!projectId && !!scenarioId,
  });
}

export function useMarketDerived(projectId, scenarioId) {
  return useQuery({
    queryKey: queryKeys.marketDerived(projectId, scenarioId),
    queryFn: () => marketAPI.getDerived(projectId, scenarioId).then(r => r.data),
    enabled: !!projectId && !!scenarioId,
  });
}

export function useMarketRadius(projectId) {
  return useQuery({
    queryKey: queryKeys.marketRadius(projectId),
    queryFn: () => marketAPI.getRadiusAnalysis(projectId).then(r => r.data),
    enabled: !!projectId,
    staleTime: 60 * 1000,
  });
}

export function useMarketLocationData(projectId) {
  return useQuery({
    queryKey: queryKeys.marketLocationData(projectId),
    queryFn: () => marketAPI.getLocationData(projectId).then(r => r.data),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMarketSnapshot(projectId, scenarioId) {
  return useQuery({
    queryKey: queryKeys.marketSnapshot(projectId, scenarioId),
    queryFn: () => marketAPI.getSnapshot(projectId, scenarioId).then(r => r.data),
    enabled: !!projectId,
    staleTime: 30 * 1000,
  });
}

export function useMarketCrossValidation(projectId, scenarioId) {
  return useQuery({
    queryKey: queryKeys.marketCrossValidation(projectId, scenarioId),
    queryFn: () => marketAPI.getCrossValidation(projectId, scenarioId).then(r => r.data),
    enabled: !!projectId && !!scenarioId,
    staleTime: 30 * 1000,
  });
}

// ═══════════════════════════════════════════
//  REPORTS
// ═══════════════════════════════════════════

export function useReports(projectId) {
  return useQuery({
    queryKey: queryKeys.reports(projectId),
    queryFn: () => reportsAPI.list(projectId).then(r => r.data),
    enabled: !!projectId,
  });
}

export function useGenerateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => reportsAPI.generate(data).then(r => r.data),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.reports(variables.project_id) });
      qc.invalidateQueries({ queryKey: queryKeys.dashboardStats });
    },
  });
}

export function useDeleteReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, projectId }) => reportsAPI.delete(id).then(r => r.data),
    onSuccess: (_, { projectId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.reports(projectId) });
    },
  });
}

// ═══════════════════════════════════════════
//  COMPANY & SETTINGS
// ═══════════════════════════════════════════

export function useCompanyUsers() {
  return useQuery({
    queryKey: queryKeys.companyUsers,
    queryFn: () => companyAPI.listUsers().then(r => r.data),
    staleTime: 30 * 1000,
  });
}

export function useEntitlements() {
  return useQuery({
    queryKey: queryKeys.entitlements,
    queryFn: () => companyAPI.getEntitlements().then(r => r.data),
    staleTime: 60 * 1000,
  });
}

export function useSettings() {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: () => settingsAPI.get().then(r => r.data),
    staleTime: 60 * 1000,
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ section, data }) => {
      if (section === 'company') return settingsAPI.updateCompany(data).then(r => r.data);
      if (section === 'financial') return settingsAPI.updateFinancial(data).then(r => r.data);
      if (section === 'reports') return settingsAPI.updateReports(data).then(r => r.data);
      return settingsAPI.updatePreferences(data).then(r => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.settings });
    },
  });
}

// ═══════════════════════════════════════════
//  STRUCTURED INPUTS
// ═══════════════════════════════════════════

export function useStructuredInputs(scenarioId) {
  return useQuery({
    queryKey: queryKeys.structuredInputs(scenarioId),
    queryFn: () => {
      const { structuredInputsAPI } = require('./api');
      return structuredInputsAPI.getAll(scenarioId).then(r => r.data);
    },
    enabled: !!scenarioId,
  });
}


// ═══════════════════════════════════════════
//  INSTITUTIONAL DASHBOARD
// ═══════════════════════════════════════════

export function useInstitution() {
  return useQuery({
    queryKey: ['institution'],
    queryFn: () => institutionAPI.get().then(r => r.data),
    staleTime: 60 * 1000,
  });
}

export function useInstitutionalDashboard() {
  return useQuery({
    queryKey: ['institutionalDashboard'],
    queryFn: () => institutionAPI.dashboard().then(r => r.data),
    staleTime: 30 * 1000,
  });
}

export function useInstitutionCompanies() {
  return useQuery({
    queryKey: ['institutionCompanies'],
    queryFn: () => institutionAPI.listCompanies().then(r => r.data),
    staleTime: 30 * 1000,
  });
}
